/**
 * Traduzione centralizzata degli errori database (Postgres/PostgREST) in messaggi
 * comprensibili per l'utente. Gli errori non riconosciuti passano invariati,
 * quindi la funzione è sicura da usare come wrapper universale negli store:
 *
 *   set({ error: friendlyDbError(error) })
 *
 * Le violazioni note vengono mappate per nome constraint; per il resto si usa
 * un messaggio generico per codice errore con il nome entità tradotto.
 */

import { useSettingsStore } from '../stores/settingsStore';

interface DbErrorLike {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
}

// Nomi leggibili delle tabelle più comuni citate negli errori FK
const TABLE_LABELS: Record<string, { it: string; en: string }> = {
  clients: { it: 'lead/clienti', en: 'leads/clients' },
  client_states: { it: 'stati cliente', en: 'client states' },
  client_state_changes: { it: 'storico stati dei clienti', en: 'client state history' },
  lead_pipelines: { it: 'pipeline', en: 'pipelines' },
  lead_pipeline_stages: { it: 'stage della pipeline', en: 'pipeline stages' },
  client_pipeline_memberships: { it: 'assegnazioni pipeline', en: 'pipeline assignments' },
  lead_sources: { it: 'fonti lead', en: 'lead sources' },
  lead_states: { it: 'stati lead', en: 'lead states' },
  deals: { it: 'offerte', en: 'quotes' },
  deal_services: { it: 'servizi delle offerte', en: 'quote services' },
  services: { it: 'servizi', en: 'services' },
  projects: { it: 'progetti', en: 'projects' },
  tasks: { it: 'task', en: 'tasks' },
  sales_activities: { it: 'task commerciali', en: 'sales tasks' },
  invoices: { it: 'fatture', en: 'invoices' },
  user_profiles: { it: 'utenti', en: 'users' },
  referrers: { it: 'segnalatori', en: 'referrers' },
  newsletter_campaigns: { it: 'campagne newsletter', en: 'newsletter campaigns' },
  workflows: { it: 'workflow', en: 'workflows' },
};

// Messaggi specifici per constraint noti (hanno priorità sul messaggio generico)
const CONSTRAINT_MESSAGES: Record<string, { it: string; en: string }> = {
  client_state_changes_to_state_id_fkey: {
    it: 'Questo stato non può essere eliminato perché fa parte dello storico di uno o più clienti. È stato disattivato invece che eliminato.',
    en: 'This state cannot be deleted because it is part of one or more clients\' history. It has been deactivated instead.',
  },
  clients_pipeline_stage_id_fkey: {
    it: 'Operazione bloccata: uno o più lead/clienti sono ancora collegati a uno stage di questa pipeline.',
    en: 'Operation blocked: one or more leads/clients are still linked to a stage of this pipeline.',
  },
  client_pipeline_memberships_pipeline_stage_id_fkey: {
    it: 'Operazione bloccata: esistono ancora assegnazioni a uno stage di questa pipeline.',
    en: 'Operation blocked: there are still assignments to a stage of this pipeline.',
  },
};

function tableLabel(table: string | null, lang: 'it' | 'en'): string | null {
  if (!table) return null;
  return TABLE_LABELS[table]?.[lang] || table.replace(/_/g, ' ');
}

function detectLanguage(): 'it' | 'en' {
  try {
    const lang = useSettingsStore.getState()?.settings?.language;
    return lang === 'en' ? 'en' : 'it';
  } catch {
    return 'it';
  }
}

/**
 * Converte un errore (PostgrestError, Error o unknown) in un messaggio leggibile.
 * Se l'errore non è riconosciuto come errore database, restituisce il messaggio originale.
 */
export function friendlyDbError(error: unknown, langOverride?: 'it' | 'en'): string {
  const err = (error || {}) as DbErrorLike;
  const rawMessage = err.message || String(error ?? '');
  const lang = langOverride || detectLanguage();

  // Estrae nome constraint e tabelle dal messaggio Postgres, es.:
  // update or delete on table "client_states" violates foreign key constraint
  // "client_state_changes_to_state_id_fkey" on table "client_state_changes"
  const constraintMatch = rawMessage.match(/constraint "([^"]+)"/);
  const constraintName = constraintMatch?.[1] || null;
  if (constraintName && CONSTRAINT_MESSAGES[constraintName]) {
    return CONSTRAINT_MESSAGES[constraintName][lang];
  }

  const isFkDeleteMessage = /update or delete on table "([^"]+)" violates foreign key/.test(rawMessage);
  const isFkInsertMessage = /insert or update on table "([^"]+)" violates foreign key/.test(rawMessage);
  const referencedTable = rawMessage.match(/on table "([^"]+)"\s*$/)?.[1]
    || rawMessage.match(/violates foreign key constraint "[^"]+" on table "([^"]+)"/)?.[1]
    || null;

  switch (err.code) {
    case '23503': // foreign_key_violation
      if (isFkInsertMessage) {
        return lang === 'it'
          ? 'Impossibile salvare: uno dei riferimenti selezionati non esiste più. Ricarica la pagina e riprova.'
          : 'Cannot save: one of the selected references no longer exists. Reload the page and try again.';
      }
      {
        const label = tableLabel(referencedTable, lang);
        return lang === 'it'
          ? `Impossibile eliminare questo elemento perché è ancora utilizzato${label ? ` da: ${label}` : ' da altri dati'}. Rimuovi prima i collegamenti e riprova.`
          : `Cannot delete this item because it is still in use${label ? ` by: ${label}` : ' by other data'}. Remove the links first and try again.`;
      }
    case '23505': // unique_violation
      return lang === 'it'
        ? 'Esiste già un elemento con questo nome o valore. Scegli un nome diverso.'
        : 'An item with this name or value already exists. Choose a different name.';
    case '23502': // not_null_violation
      return lang === 'it'
        ? 'Manca un campo obbligatorio. Compila tutti i campi richiesti e riprova.'
        : 'A required field is missing. Fill in all required fields and try again.';
    case '23514': // check_violation
      return lang === 'it'
        ? 'Uno dei valori inseriti non è valido. Controlla i dati e riprova.'
        : 'One of the entered values is not valid. Check the data and try again.';
    case '42501': // insufficient_privilege (RLS/GRANT)
      return lang === 'it'
        ? 'Non hai i permessi per eseguire questa operazione. Contatta un amministratore.'
        : 'You do not have permission to perform this operation. Contact an administrator.';
    case '22P02': // invalid_text_representation
      return lang === 'it'
        ? 'Uno dei valori inseriti ha un formato non valido. Controlla i dati e riprova.'
        : 'One of the entered values has an invalid format. Check the data and try again.';
    case '57014': // query_canceled (statement timeout)
      return lang === 'it'
        ? 'L\'operazione ha impiegato troppo tempo ed è stata annullata. Riprova tra qualche istante.'
        : 'The operation took too long and was cancelled. Try again in a moment.';
    case 'PGRST116': // .single() senza righe (o con più righe)
      return lang === 'it'
        ? 'Elemento non trovato. Potrebbe essere stato eliminato: ricarica la pagina.'
        : 'Item not found. It may have been deleted: reload the page.';
  }

  // Messaggi FK senza codice (es. errore ributtato come Error generico)
  if (isFkDeleteMessage || isFkInsertMessage) {
    const label = tableLabel(referencedTable, lang);
    return lang === 'it'
      ? `Operazione bloccata: l'elemento è ancora collegato ad altri dati${label ? ` (${label})` : ''}.`
      : `Operation blocked: the item is still linked to other data${label ? ` (${label})` : ''}.`;
  }

  return rawMessage;
}

/** True se l'errore è una violazione di foreign key (utile per fallback soft-delete). */
export function isForeignKeyViolation(error: unknown): boolean {
  const err = (error || {}) as DbErrorLike;
  return err.code === '23503' || /violates foreign key constraint/.test(err.message || '');
}
